import { useState, useEffect, useRef } from 'react';

const AboutPage = ({ onClose, animationStage, onNavigate }) => {
  const [contentStage, setContentStage] = useState('entering');
  const [activeFounder, setActiveFounder] = useState(null);
  const pageRef = useRef(null);
  const founderRefs = useRef({});

  // Founders data with real images (geometry attribute removed)
  const founders = [
    {
      id: 'baraka',
      name: 'Baraka Fadhili',
      role: 'Chief Executive Officer',
      email: 'fadhili.njagi@nixque.com',
      phone: '+254 788 979327',
      skills: ['Software Engineering', 'Cybersecurity', 'Data Science & AI', 'Accounting & Finance'],
      bio: 'Visionary leader with expertise in computer science and software engineering. Driving innovation through strategic technology solutions that bridge healthcare and technology.',
      color: 'from-cyan-500 to-blue-500',
      image: 'https://miro.medium.com/v2/resize:fill:72:72/1*4tHwTm3Y-ps8iKk6PLGI0g.jpeg'
    },
    {
      id: 'grold',
      name: 'Grold O. Mboya', 
      role: 'Chief Technology Officer',
      email: 'grold.mboya@nixque.com',
      phone: '+254 742 606971',
      skills: [ 'Software Engineering','Biostatistics', 'Data Science','Epidemiology'],
      bio: 'Expert in software engineering, quantitative epidemiology, quant and biostatistical modelling, bringing data-driven insights and research methodology to create impactful technological solutions for global health challenges.',
      color: 'from-purple-500 to-fuchsia-500',
      image: 'https://eu-central-1.graphassets.com/Ah1sGJMcWRmCux6Ex9ergz/XeVtGFFYRlGTtKQhv1fv'
    }
  ];

  useEffect(() => {
    if (animationStage === 'entering') {
      setContentStage('entering');
      const timer = setTimeout(() => setContentStage('visible'), 100);
      return () => clearTimeout(timer);
    } else if (animationStage === 'exiting') {
      setContentStage('exiting');
    }
  }, [animationStage]);

  const handleFounderClick = (founderId) => {
    const wasActive = activeFounder === founderId;
    setActiveFounder(wasActive ? null : founderId);
    
    // Smooth scroll to founder section when expanding
    if (!wasActive && founderRefs.current[founderId]) {
      setTimeout(() => {
        founderRefs.current[founderId].scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  };

  const setFounderRef = (founderId, element) => {
    founderRefs.current[founderId] = element;
  };

  return (
    <div 
      ref={pageRef}
      className="fixed inset-0 z-50 bg-slate-950 overflow-y-auto"
    >
      {/* Enhanced Quantum Background */}
      <QuantumBackground />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/70">
  <div className="max-w-7xl mx-auto px-6 py-4">
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-3">
        <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
        <div className="flex items-center gap-3 text-white font-mono text-lg tracking-tight">
          <img 
            src="/icons/favicon-32x32.png" 
            alt="Nixque Icon" 
            className="w-6 h-6"
            onLoad={(e) => {
              console.log('✅ IMAGE LOADED SUCCESSFULLY!');
              console.log('Full URL:', e.target.src);
              console.log('Dimensions:', e.target.naturalWidth, 'x', e.target.naturalHeight);
            }}
            onError={(e) => {
              console.log('Attempted URL:', e.target.src);
              console.log('Trying fallback paths...');
              
              // Try different paths
              if (!e.target.dataset.retry1) {
                e.target.dataset.retry1 = 'true';
                console.log('Trying /favicon.ico');
                e.target.src = "/favicon.ico";
              } else if (!e.target.dataset.retry2) {
                e.target.dataset.retry2 = 'true';
                console.log('Trying /icons/favicon.ico');
                e.target.src = "/icons/favicon.ico";
              } else if (!e.target.dataset.retry3) {
                e.target.dataset.retry3 = 'true';
                console.log('Trying /icons/icon-32x32.png');
                e.target.src = "/icons/icon-32x32.png";
              } else {
                console.log('All paths failed, showing placeholder');
                e.target.style.display = 'none';
                const placeholder = document.createElement('div');
                placeholder.className = 'w-6 h-6 bg-cyan-400 rounded flex items-center justify-center border-2 border-green-500';
                placeholder.innerHTML = '<span class="text-[8px] font-bold text-gray-900">IMG</span>';
                e.target.parentNode.appendChild(placeholder);
              }
            }}
          />
          <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            NIXQUE LAB
          </span>
        </div>
      </div>
      <button
        onClick={onClose}
        className="group px-6 py-3 rounded-xl border border-slate-700 text-white hover:bg-slate-800/50 transition-all duration-300 font-mono text-sm tracking-wide hover:border-cyan-400/50 hover:text-cyan-300 backdrop-blur-sm flex items-center gap-2"
      >
        <span>RETURN TO MAIN</span>
        <div className="group-hover:translate-x-1 transition-transform duration-200">
          ↗
        </div>
      </button>
    </div>
  </div>
</nav>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Enhanced Hero Section */}
        <section className="min-h-screen relative flex items-center justify-center overflow-hidden pt-20">
          <div className="max-w-7xl mx-auto px-6 py-16 w-full">
            <div className={`transition-all duration-1000 ${
              contentStage === 'entering' 
                ? 'opacity-0 transform translate-y-12 blur-sm' 
                : 'opacity-100 transform translate-y-0 blur-0'
            }`}>
              
              {/* Improved Split-Word Title */}
              <EnhancedSplitWordTitle />
              
              {/* Company Subtitle */}
              <div className="text-center mt-12 lg:mt-20">
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-6 lg:mb-8 font-mono bg-gradient-to-r from-cyan-400 via-purple-400 to-amber-400 bg-clip-text text-transparent">
                  ABOUT NIXQUE LTD
                </h2>
                
                <p className="text-lg lg:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed px-4">
                  Where <span className="text-cyan-400 font-bold">Computer Science</span> meets{' '}
                  <span className="text-purple-400 font-bold">Statistics</span> to create{' '}
                  <span className="text-amber-400 font-bold">revolutionary technology solutions</span> for{' '}
                  <span className="text-green-400 font-bold">tomorrow's challenges</span>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Founders Section */}
        <section className="py-20 lg:py-32 relative">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl lg:text-4xl font-bold text-white text-center mb-12 lg:mb-20 font-mono">
              THE ARCHITECTS
            </h2>
            
            {/* Interactive DNA Strand Layout */}
            <div className="relative">
              {/* Connecting DNA Line (Moved a bit to account for larger frames) */}
              <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-400/30 via-purple-400/30 to-transparent transform -translate-x-1/2 hidden lg:block"></div>
              
              <div className="space-y-12 lg:space-y-20">
                {founders.map((founder, index) => (
                  <div key={founder.id} ref={el => setFounderRef(founder.id, el)}>
                    <DNAFounderNode
                      founder={founder}
                      index={index}
                      isActive={activeFounder === founder.id}
                      onClick={handleFounderClick}
                      contentStage={contentStage}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Vision & Principles Section (Replaced Cliched Cards) */}
        <section className="py-20 lg:py-32 border-y border-slate-800/50 bg-slate-900/20 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-6">
            <EnhancedVisionPrinciples contentStage={contentStage} />
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 lg:py-32">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className={`transition-all duration-1000 ${
              contentStage === 'entering' 
                ? 'opacity-0 transform translate-y-8' 
                : 'opacity-100 transform translate-y-0'
            }`}>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 lg:mb-8 font-mono">
                OUR MISSION
              </h2>
              <p className="text-base lg:text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto">
                To harness the power of Computer Science and Statistics, creating intelligent systems 
                that solve complex problems and improve lives through technology that understands both 
                data and human needs. We bridge the gap between cutting-edge technology and real-world 
                health challenges to create solutions that matter.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Enhanced Footer Section */}
      <Footer onNavigate={onNavigate} />

      <style jsx>{`
        @keyframes float-quantum {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.3;
          }
          33% { 
            transform: translateY(-15px) rotate(120deg) scale(1.1);
            opacity: 0.6;
          }
          66% { 
            transform: translateY(10px) rotate(240deg) scale(0.9);
            opacity: 0.4;
          }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.3; }
        }
        @keyframes orb-particle {
          0%, 100% {
            transform: scale(0) rotate(0deg);
            opacity: 0;
          }
          50% {
            transform: scale(1) rotate(180deg);
            opacity: 1;
          }
        }
        @keyframes dna-float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-5px) rotate(5deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-float-quantum {
          animation: float-quantum 8s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
        .animate-orb-particle {
          animation: orb-particle 2s ease-in-out infinite;
        }
        .animate-dna-float {
          animation: dna-float 6s ease-in-out infinite;
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

// Quantum Background Component (No change)
const QuantumBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Quantum Grid */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.1)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]"></div>
      </div>

      {/* Floating Quantum Particles */}
      <div className="absolute inset-0">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-cyan-500/20 animate-float-quantum"
            style={{
              width: `${20 + i * 15}px`,
              height: `${20 + i * 15}px`,
              left: `${(i * 12) % 100}%`,
              top: `${(i * 15) % 100}%`,
              animationDelay: `${i * 2}s`,
              filter: 'blur(12px)',
            }}
          />
        ))}
      </div>

      {/* Geometric Shapes (Using simple circles/squares now) */}
      <div className="absolute inset-0">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`absolute border border-cyan-400/30 animate-pulse-slow ${
              i % 2 === 0 ? 'rounded-full' : 'rounded-lg'
            }`}
            style={{
              width: '80px',
              height: '80px',
              left: `${(i * 20) % 100}%`,
              top: `${(i * 25) % 100}%`,
              animationDelay: `${i * 1.5}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Enhanced Split Word Title Component (No change)
const EnhancedSplitWordTitle = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="w-full mb-12 lg:mb-16 px-4">
      <h1 className="enhanced-split-title">
        {/* Line 1 */}
        <span className="text-word">Championing</span>
        
        <QuantumImageWord 
          imageSrc="https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=200&h=200&fit=crop"
          alt="Innovation"
          isVisible={isVisible}
          delay={0}
        />
        
        <br className="block lg:hidden" />
        
        {/* Line 2 */}
        <span className="text-word">Technological</span>
        <QuantumImageWord 
          imageSrc="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=200&h=200&fit=crop"
          alt="Technology"
          isVisible={isVisible}
          delay={100}
        />
        <span className="text-word">Evolution</span>
        
        <br />
        
        {/* Line 3 */}
        <QuantumImageWord 
          imageSrc="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200&h=200&fit=crop"
          alt="Global"
          isVisible={isVisible}
          delay={200}
        />
        <span className="text-word">Solutions</span>
      </h1>

      <style jsx>{`
        .enhanced-split-title {
          font-family: system-ui, -apple-system, sans-serif;
          font-style: normal;
          font-weight: 350;
          line-height: 1.1;
          letter-spacing: -0.02em;
          font-size: clamp(2rem, 5vw, 4rem);
          text-align: center;
          color: white;
          margin: 0;
          padding: 0;
        }

        .text-word {
          display: inline-block;
          margin: 0 0.05em;
          background: linear-gradient(135deg, #ffffff, #e2e8f0);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 350;
          white-space: nowrap;
        }

        @media (max-width: 640px) {
          .enhanced-split-title {
            font-size: clamp(1.8rem, 6vw, 3rem);
            line-height: 1.2;
            letter-spacing: -0.01em;
          }
          
          .text-word {
            margin: 0 0.02em;
          }
        }

        @media (min-width: 1024px) {
          .enhanced-split-title {
            font-size: clamp(3rem, 4vw, 4.5rem);
          }
        }
      `}</style>
    </div>
  );
};

// Quantum Image Word Component (Simplified, Minimal Frame - addresses "round frames" complaint)
const QuantumImageWord = ({ imageSrc, alt, isVisible, delay }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <span className="quantum-image-word">
      <div className="quantum-image-container">
        
        {/* Simplified Minimal Frame with Glass Effect */}
        <div className="minimal-frame">
          <img
            src={imageSrc}
            alt={alt}
            className={`quantum-image ${imageLoaded ? 'loaded' : ''}`}
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1) rotate(0deg)' : 'scale(0.8) rotate(-5deg)',
              transitionDelay: `${delay}ms`,
            }}
            onLoad={() => setImageLoaded(true)}
          />
        </div>

      </div>

      <style jsx>{`
        .quantum-image-word {
          display: inline-block;
          position: relative;
          margin: 0 0.1em;
          vertical-align: middle;
        }

        .quantum-image-container {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: clamp(50px, 9vw, 70px); 
          height: clamp(50px, 9vw, 70px);
          padding: 3px;
          overflow: hidden;
        }
        
        .minimal-frame {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 8px; /* Square/Rounded */
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 0 10px rgba(0, 255, 255, 0.2);
          overflow: hidden;
          padding: 2px;
          transition: all 0.3s ease;
          z-index: 1;
        }

        .quantum-image {
          position: relative;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 6px;
          transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
          z-index: 3;
          filter: brightness(1.1) contrast(1.1);
        }
        
        .quantum-image-word:hover .minimal-frame {
          border-color: #06b6d4;
          box-shadow: 0 0 20px rgba(6, 182, 212, 0.5);
        }

        .quantum-image-word:hover .quantum-image {
          transform: scale(1.05) rotate(0deg);
          filter: brightness(1.3) contrast(1.2);
        }

        @media (max-width: 640px) {
          .quantum-image-container {
            width: clamp(40px, 8vw, 60px);
            height: clamp(40px, 8vw, 60px);
          }
        }
      `}</style>
    </span>
  );
};

// DNA Founder Node Component with Enhanced Features (Simplified image frame - addresses "thick hexagonal shapes" complaint)
const DNAFounderNode = ({ founder, index, isActive, onClick, contentStage }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Format bio with line breaks for better readability
  const formatBio = (bio) => {
    // For Grold's bio, split into multiple lines
    if (bio.includes('biostatistical modelling,')) {
      return [
        'Expert in software engineering, quantitative epidemiology,',
        'quant and biostatistical modelling, bringing data-driven insights',
        'and research methodology to create impactful technological solutions',
        'for global health challenges.'
      ];
    }
    // For Baraka's bio, split by sentences
    return bio.split('. ').map((sentence, index, array) => 
      index === array.length - 1 ? sentence : `${sentence}.`
    );
  };

  // CORRECTED: The error was here, accessing index [2] instead of [1]
  const [fromColor, toColor] = founder.color.split(' ');
  const gradientStyle = { 
    background: `linear-gradient(to right, ${fromColor.replace('from-', '#')}, ${toColor.replace('to-', '#')})` 
  };
  
  return (
    <div 
      className={`relative transition-all duration-1000 ${
        contentStage === 'entering' 
          ? 'opacity-0 transform translate-y-8' 
          : 'opacity-100 transform translate-y-0'
      }`}
      style={{
        transitionDelay: contentStage === 'visible' ? `${index * 400}ms` : '0ms'
      }}
    >
      {/* Main Node Container */}
      <div 
        className={`relative group cursor-pointer ${
          index % 2 === 0 ? 'lg:pr-1/2' : 'lg:pl-1/2 lg:flex-row-reverse'
        }`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onClick(founder.id)} // Make the whole section clickable
      >
        {/* Content Container */}
        <div className={`flex flex-col lg:flex-row items-center gap-6 lg:gap-8 ${
          index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
        }`}>
          
          {/* Circular/Rounded Image Frame (Modernized) */}
          <div className="relative flex-shrink-0">
            <div 
              className={`relative w-32 h-32 lg:w-40 lg:h-40 p-1 rounded-full transition-all duration-700 mx-auto ${
                isActive ? 'scale-105 shadow-2xl shadow-cyan-500/30' : 'scale-100 group-hover:scale-105'
              }`}
              style={gradientStyle} // Using the fixed gradientStyle
            >
              
              {/* Inner container to create border effect */}
              <div className="w-full h-full rounded-full overflow-hidden bg-slate-950/70 p-1">
                <img
                  src={founder.image}
                  alt={founder.name}
                  className={`w-full h-full object-cover rounded-full transition-all duration-700 ${
                    imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
                  } ${isActive ? 'grayscale-0' : 'grayscale group-hover:grayscale-0'}`}
                  onLoad={() => setImageLoaded(true)}
                />
                {!imageLoaded && (
                  <div className={`absolute inset-0 rounded-full bg-gradient-to-r from-slate-700 via-slate-600 to-slate-700 animate-pulse flex items-center justify-center`}>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-500/20 to-transparent animate-shimmer"></div>
                    <div className="text-slate-400 text-3xl font-bold relative z-10">
                      {founder.name.split(' ')[0][0]}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* DNA Connection Node (Subtle) */}
            <div className={`absolute top-1/2 -translate-y-1/2 hidden lg:block ${
              index % 2 === 0 ? 'right-0 translate-x-[4.3rem]' : 'left-0 -translate-x-[4.3rem]' // Aligns it visually to the connecting line
            }`}>
              <div className={`w-4 h-4 rounded-full border-2 transition-all duration-300 animate-dna-float ${
                isActive ? `bg-cyan-400 border-cyan-300` : 'bg-slate-700 border-slate-600 group-hover:bg-cyan-400/50'
              }`}></div>
            </div>
          </div>

          {/* Information Panel */}
          <div className={`flex-1 text-center lg:text-left ${
            index % 2 === 0 ? 'lg:text-left' : 'lg:text-right'
          }`}>
            
            {/* Name and Role */}
            <div className="mb-4">
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2 font-mono">
                {founder.name}
              </h3>
              <p className={`text-lg lg:text-xl font-mono bg-gradient-to-br ${founder.color} bg-clip-text text-transparent`}>
                {founder.role}
              </p>
            </div>

            {/* Skills DNA Strand */}
            <div className={`flex flex-wrap gap-2 mb-4 ${
              index % 2 === 0 
                ? 'justify-center lg:justify-start' 
                : 'justify-center lg:justify-end'
            }`}>
              {founder.skills.map((skill, skillIndex) => (
                <span
                  key={skill}
                  className={`px-3 py-1 text-xs bg-slate-800/50 text-slate-400 rounded-full border transition-all duration-300 hover:scale-105 ${
                    isActive ? `border-cyan-400/50 text-cyan-300` : 'border-slate-700/50 group-hover:border-cyan-400/50'
                  }`}
                  style={{
                    animationDelay: `${skillIndex * 0.1}s`
                  }}
                >
                  {skill}
                </span>
              ))}
            </div>

            {/* Contact Links */}
            <div className={`flex flex-col sm:flex-row gap-3 mb-4 ${
              index % 2 === 0 
                ? 'justify-center lg:justify-start' 
                : 'items-end sm:justify-center lg:justify-end'
            }`}>
              <a
                href={`mailto:${founder.email}`}
                className="text-cyan-400 hover:text-cyan-300 transition-colors duration-300 text-sm font-mono flex items-center gap-2 hover:translate-x-1 transform transition-transform"
                onClick={(e) => e.stopPropagation()}
              >
                <span>✉️</span>
                {founder.email}
              </a>
              <a
                href={`tel:${founder.phone}`}
                className="text-cyan-400 hover:text-cyan-300 transition-colors duration-300 text-sm font-mono flex items-center gap-2 hover:translate-x-1 transform transition-transform"
                onClick={(e) => e.stopPropagation()}
              >
                <span>📞</span>
                {founder.phone}
              </a>
            </div>

            {/* Expandable Bio - Updated with shorter lines */}
            <div className={`transition-all duration-500 overflow-hidden ${
              isActive ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
            }`}>
              <div className="pt-4 border-t border-slate-700/50">
                <div className={`text-slate-300 text-sm leading-relaxed space-y-2 ${
                  index % 2 === 0 
                    ? 'text-left lg:text-left' 
                    : 'text-left lg:text-right'
                }`}>
                  {formatBio(founder.bio).map((line, lineIndex) => (
                    <p key={lineIndex} className="m-0">
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            </div>

            {/* Interactive Hint with Enhanced Accessibility */}
            {!isActive && (
              <div className={`mt-3 ${index % 2 === 0 ? 'text-left' : 'text-right'}`}>
                <button
                  onClick={() => onClick(founder.id)}
                  className="text-slate-500 text-xs font-mono animate-pulse hover:text-cyan-400 transition-colors duration-300"
                  aria-expanded={isActive}
                  aria-label={`Expand ${founder.name}'s bio`}
                >
                  {index % 2 === 0 ? 'Click to explore ' : ''}{founder.name.split(' ')[0]}'s expertise{index % 2 === 0 ? '' : ' ← Click to explore'}
                </button>
              </div>
            )}
            {isActive && (
              <div className={`mt-3 ${index % 2 === 0 ? 'text-left' : 'text-right'}`}>
                <button
                  onClick={() => onClick(founder.id)}
                  className="text-cyan-400 text-xs font-mono hover:text-cyan-300 transition-colors duration-300"
                  aria-expanded={isActive}
                  aria-label={`Collapse ${founder.name}'s bio`}
                >
                  {index % 2 === 0 ? 'Collapse details ' : 'Collapse details '}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Hover Effect Glow - Simplified */}
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${founder.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10`}></div>
      </div>
    </div>
  );
};

// Enhanced Split Word Title Component (No change)
// const EnhancedSplitWordTitle = () => {
//   const [isVisible, setIsVisible] = useState(false);
//   const sectionRef = useRef(null);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting) {
//           setIsVisible(true);
//         }
//       },
//       { threshold: 0.2 }
//     );

//     if (sectionRef.current) {
//       observer.observe(sectionRef.current);
//     }

//     return () => observer.disconnect();
//   }, []);

//   return (
//     <div ref={sectionRef} className="w-full mb-12 lg:mb-16 px-4">
//       <h1 className="enhanced-split-title">
//         {/* Line 1 */}
//         <span className="text-word">Championing</span>
        
//         <QuantumImageWord 
//           imageSrc="https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=200&h=200&fit=crop"
//           alt="Innovation"
//           isVisible={isVisible}
//           delay={0}
//         />
        
//         <br className="block lg:hidden" />
        
//         {/* Line 2 */}
//         <span className="text-word">Technological</span>
//         <QuantumImageWord 
//           imageSrc="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=200&h=200&fit=crop"
//           alt="Technology"
//           isVisible={isVisible}
//           delay={100}
//         />
//         <span className="text-word">Evolution</span>
        
//         <br />
        
//         {/* Line 3 */}
//         <QuantumImageWord 
//           imageSrc="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200&h=200&fit=crop"
//           alt="Global"
//           isVisible={isVisible}
//           delay={200}
//         />
//         <span className="text-word">Solutions</span>
//       </h1>

//       <style jsx>{`
//         .enhanced-split-title {
//           font-family: system-ui, -apple-system, sans-serif;
//           font-style: normal;
//           font-weight: 350;
//           line-height: 1.1;
//           letter-spacing: -0.02em;
//           font-size: clamp(2rem, 5vw, 4rem);
//           text-align: center;
//           color: white;
//           margin: 0;
//           padding: 0;
//         }

//         .text-word {
//           display: inline-block;
//           margin: 0 0.05em;
//           background: linear-gradient(135deg, #ffffff, #e2e8f0);
//           background-clip: text;
//           -webkit-background-clip: text;
//           -webkit-text-fill-color: transparent;
//           font-weight: 350;
//           white-space: nowrap;
//         }

//         @media (max-width: 640px) {
//           .enhanced-split-title {
//             font-size: clamp(1.8rem, 6vw, 3rem);
//             line-height: 1.2;
//             letter-spacing: -0.01em;
//           }
          
//           .text-word {
//             margin: 0 0.02em;
//           }
//         }

//         @media (min-width: 1024px) {
//           .enhanced-split-title {
//             font-size: clamp(3rem, 4vw, 4.5rem);
//           }
//         }
//       `}</style>
//     </div>
//   );
// };

// // Quantum Image Word Component (Simplified, Minimal Frame - addresses "round frames" complaint)
// const QuantumImageWord = ({ imageSrc, alt, isVisible, delay }) => {
//   const [imageLoaded, setImageLoaded] = useState(false);

//   return (
//     <span className="quantum-image-word">
//       <div className="quantum-image-container">
        
//         {/* Simplified Minimal Frame with Glass Effect */}
//         <div className="minimal-frame">
//           <img
//             src={imageSrc}
//             alt={alt}
//             className={`quantum-image ${imageLoaded ? 'loaded' : ''}`}
//             style={{
//               opacity: isVisible ? 1 : 0,
//               transform: isVisible ? 'scale(1) rotate(0deg)' : 'scale(0.8) rotate(-5deg)',
//               transitionDelay: `${delay}ms`,
//             }}
//             onLoad={() => setImageLoaded(true)}
//           />
//         </div>

//       </div>

//       <style jsx>{`
//         .quantum-image-word {
//           display: inline-block;
//           position: relative;
//           margin: 0 0.1em;
//           vertical-align: middle;
//         }

//         .quantum-image-container {
//           position: relative;
//           display: inline-flex;
//           align-items: center;
//           justify-content: center;
//           width: clamp(50px, 9vw, 70px); 
//           height: clamp(50px, 9vw, 70px);
//           padding: 3px;
//           overflow: hidden;
//         }
        
//         .minimal-frame {
//           position: relative;
//           width: 100%;
//           height: 100%;
//           border-radius: 8px; /* Square/Rounded */
//           background: rgba(255, 255, 255, 0.05);
//           backdrop-filter: blur(5px);
//           border: 1px solid rgba(255, 255, 255, 0.1);
//           box-shadow: 0 0 10px rgba(0, 255, 255, 0.2);
//           overflow: hidden;
//           padding: 2px;
//           transition: all 0.3s ease;
//           z-index: 1;
//         }

//         .quantum-image {
//           position: relative;
//           width: 100%;
//           height: 100%;
//           object-fit: cover;
//           border-radius: 6px;
//           transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
//           z-index: 3;
//           filter: brightness(1.1) contrast(1.1);
//         }
        
//         .quantum-image-word:hover .minimal-frame {
//           border-color: #06b6d4;
//           box-shadow: 0 0 20px rgba(6, 182, 212, 0.5);
//         }

//         .quantum-image-word:hover .quantum-image {
//           transform: scale(1.05) rotate(0deg);
//           filter: brightness(1.3) contrast(1.2);
//         }

//         @media (max-width: 640px) {
//           .quantum-image-container {
//             width: clamp(40px, 8vw, 60px);
//             height: clamp(40px, 8vw, 60px);
//           }
//         }
//       `}</style>
//     </span>
//   );
// };

// Enhanced Vision Principles Component (Manifesto Style - addresses "card system" complaint)
const EnhancedVisionPrinciples = ({ contentStage }) => {
  const principles = [
    {
      code: "// CODE: Innovation-Driven-Design (IDD)",
      description: "We engineer solutions from the ground up, combining cutting-edge technology with proven scientific principles."
    },
    {
      code: "// CODE: Global-Scale-Architecture (GSA)",
      description: "Our work transcends boundaries, creating solutions that address universal challenges in healthcare and technology."
    },
    {
      code: "// CODE: Adaptive-Evolution-Framework (AEF)",
      description: "We move at the speed of innovation, constantly adapting and evolving to stay ahead of technological curves."
    },
    {
      code: "// CODE: Empirical-Validation-Protocol (EVP)",
      description: "Every solution is backed by empirical evidence and rigorous testing methodologies."
    }
  ];
  
  const [activePrinciple, setActivePrinciple] = useState(0);

  useEffect(() => {
    if (contentStage === 'visible') {
      const interval = setInterval(() => {
        setActivePrinciple(prev => (prev + 1) % principles.length);
      }, 5000); // Cycle every 5 seconds
      return () => clearInterval(interval);
    }
  }, [contentStage, principles.length]);

  return (
    <div className={`transition-all duration-1000 ${
      contentStage === 'entering' 
        ? 'opacity-0 transform translate-y-8' 
        : 'opacity-100 transform translate-y-0'
    }`}>
      <div className="text-center mb-12 lg:mb-16">
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 lg:mb-6 font-mono">
          THE NIXQUE MANIFESTO
        </h2>
        <p className="text-slate-400 text-base lg:text-lg max-w-2xl mx-auto px-4">
          Where computational excellence meets statistical precision to redefine what's possible.
        </p>
      </div>

      <div className="relative p-6 lg:p-10 border border-slate-700/50 bg-slate-900/40 rounded-xl shadow-2xl shadow-cyan-900/20">
        <div className="absolute inset-0 bg-slate-950/50 [mask-image:linear-gradient(to_top,transparent_1%,black_30%)]"></div>
        
        <div className="space-y-6 relative z-10">
          {principles.map((principle, index) => (
            <div 
              key={principle.code}
              className={`font-mono transition-all duration-500 cursor-pointer p-3 rounded-lg ${
                index === activePrinciple 
                  ? 'bg-cyan-400/10 border-l-4 border-cyan-400 shadow-lg shadow-cyan-500/10'
                  : 'text-slate-500 hover:text-slate-300 hover:border-l-4 hover:border-slate-600'
              }`}
              onMouseEnter={() => setActivePrinciple(index)}
              onClick={() => setActivePrinciple(index)}
            >
              <p className={`text-xs uppercase font-bold mb-1 ${
                index === activePrinciple ? 'text-cyan-300' : 'text-slate-600'
              }`}>
                {principle.code}
              </p>
              <p className={`text-sm leading-relaxed ${
                index === activePrinciple ? 'text-white' : 'text-slate-500'
              }`}>
                {principle.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Footer Component with Clickable Navigation (No change)
const Footer = ({ onNavigate }) => {
  const [currentYear] = useState(new Date().getFullYear());

  const socialLinks = [
    {
      name: 'X',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      url: 'https://x.com/nixque'
    },
    {
      name: 'Instagram',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM12 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      ),
      url: 'https://instagram.com/nixque'
    },
    {
      name: 'Facebook',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      url: 'https://facebook.com/nixque'
    },
    {
      name: 'Reddit',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
        </svg>
      ),
      url: 'https://reddit.com/r/nixque'
    }
  ];

  const quickLinks = [
    { name: 'What We Do', page: 'what-we-do' },
    { name: 'About Us', page: 'about' },
    { name: 'Articles', page: 'articles' },
    { name: 'Contact', page: 'contact' }
  ];

  const handleNavigation = (page) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  return (
    <footer className="relative border-t border-slate-800 bg-slate-950/50 backdrop-blur-xl">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/20 pointer-events-none"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          
          {/* Brand Column */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
              <div className="text-white font-mono text-lg font-bold tracking-tight bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                NIXQUE
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md mb-6">
              Building the future through innovative technology solutions. 
              We create digital experiences that transform businesses and empower growth.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg border border-slate-800 bg-slate-900/30 text-slate-400 hover:text-cyan-400 hover:border-cyan-400/30 hover:bg-cyan-400/10 transition-all duration-300"
                  aria-label={`Follow us on ${social.name}`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-mono text-sm font-semibold mb-4 uppercase tracking-wider">
              Navigation
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => handleNavigation(link.page)}
                    className="text-slate-400 hover:text-cyan-400 text-sm transition-colors duration-300 font-mono hover:underline cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-mono text-sm font-semibold mb-4 uppercase tracking-wider">
              Connect
            </h3>
            <div className="space-y-2 text-sm">
              <a
                href="mailto:contact@nixque.com"
                className="block text-slate-400 hover:text-cyan-400 transition-colors duration-300 font-mono"
              >
                contact@nixque.com
              </a>
              <a
                href="tel:+15551234567"
                className="block text-slate-400 hover:text-cyan-400 transition-colors duration-300 font-mono"
              >
                +2547 42 606971
              </a>
              <p className="text-slate-400 font-mono">
                Nairobi | Kisumu, Kenya
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <div className="text-slate-500 text-sm font-mono">
              © {currentYear} NIXQUE LTD. All rights reserved.
            </div>

            {/* Legal Links */}
            <div className="flex gap-6 text-sm">
              <button
                onClick={() => handleNavigation('privacy')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Privacy
              </button>
              <button
                onClick={() => handleNavigation('terms')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Terms
              </button>
              <button
                onClick={() => handleNavigation('cookies')}
                className="text-slate-500 hover:text-slate-300 transition-colors duration-300 font-mono"
              >
                Cookies
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default AboutPage;